import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { BreadcrumbsProps } from '@helpers/Models';
import {
  speciesSubsteps,
  productSubsteps,
  speciesBreadCrumbs,
  storeBreadcrumbs,
  OperationSubsteps,
  ManagementSubsteps,
  storeSetupSubsteps,
  storeSettingsSubsteps,
  storeSpeciesAndProductBreadcrumbs,
} from '@helpers/Constants';

/**
 * Breadcrumbs component for rendering main and sub navigation steps with optional back navigation.
 *
 * This component supports onboarding flows, store settings, and species/product setup flows,
 * adjusting its navigation and breadcrumb data depending on `fromSettings` and `forStoreSetting` flags.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.title - Page or section title, displayed next to the back button (hidden when `fromSettings` is true).
 * @param {string} props.activeMainNav - Name of the currently active main navigation tab (used for highlighting).
 * @param {number} props.activeStep - The currently active sub-navigation step number.
 * @param {Array<{name: string, step: number, path: string}>} props.subNavSteps - List of sub-navigation steps, each with a name, step number, and navigation path.
 * @param {() => void} [props.onBack] - Optional callback triggered when the back button is clicked. Defaults to `navigate(-1)` if not provided.
 * @param {boolean} [props.fromSettings] - If true, component renders with store/species settings layout (affects breadcrumbs and navigation mapping).
 * @param {boolean} [props.forStoreSetting] - Differentiates store settings (Store, Roles/Users, Operations) from species/product settings.
 *
 * @returns {JSX.Element} The rendered breadcrumb navigation UI.
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  title,
  activeMainNav,
  activeStep,
  subNavSteps,
  onBack,
  fromSettings, // To get side nav and header layout
  forStoreSetting, // for differentiating my store settings
}) => {
  const navigate = useNavigate();
  const [breadcrumbs, setBreadcrumbs] = useState<
    {
      name: string;
      stepId: number;
    }[]
  >();

  /**
   * Initializes the breadcrumbs state on component mount.
   *
   * - If `fromSettings` is true:
   *   - When `forStoreSetting` is true → loads `storeBreadcrumbs`.
   *   - Otherwise → loads `storeSpeciesAndProductBreadcrumbs`.
   * - If `fromSettings` is false:
   *   - Loads `speciesBreadCrumbs`.
   *
   * Runs only once when the component mounts (empty dependency array).
   */
  useEffect(() => {
    if (fromSettings) {
      if (forStoreSetting) {
        setBreadcrumbs(storeBreadcrumbs);
      } else {
        setBreadcrumbs(storeSpeciesAndProductBreadcrumbs);
      }
    } else {
      setBreadcrumbs(speciesBreadCrumbs);
    }
  }, []);

  /**
   * Handles main navigation tab clicks and updates breadcrumbs accordingly.
   *
   * Determines the correct navigation path based on the `tab` number and the
   * current context (`fromSettings` and `forStoreSetting` flags).
   *
   * Navigation maps:
   * - **Onboarding (`!fromSettings`)**:
   *   1. Store Setup → `storeSetupSubsteps[0].path`
   *   2. Species → `speciesSubsteps[0].path`
   *   3. Product → `productSubsteps[0].path`
   *   4. Management → `ManagementSubsteps[0].path`
   *   5. Operation → `OperationSubsteps[0].path`
   *
   * - **From Settings (species/product) (`fromSettings && !forStoreSetting`)**:
   *   1. Species → `speciesSubsteps[0].path`
   *   2. Product → `productSubsteps[0].path`
   *
   * - **Store Settings (`fromSettings && forStoreSetting`)**:
   *   1. Store Settings → `storeSettingsSubsteps[0].path`
   *   2. Management → `ManagementSubsteps[0].path`
   *   3. Operation → `OperationSubsteps[0].path`
   *
   * After navigation:
   * - Updates breadcrumbs to either `storeBreadcrumbs`, `storeSpeciesAndProductBreadcrumbs`, or `speciesBreadCrumbs`
   *   depending on the context.
   *
   * @param {number} tab - The selected tab index (1-based).
   */
  const handleMainNav = (tab: number): void => {
    // Onboarding tabs
    const navigationMap = {
      1: storeSetupSubsteps[0].path,
      2: speciesSubsteps[0].path,
      3: productSubsteps[0].path,
      4: ManagementSubsteps[0].path,
      5: OperationSubsteps[0].path,
    };

    // Species and Product tabs
    const fromSettingsNavigationMap = {
      1: speciesSubsteps[0].path,
      2: productSubsteps[0].path,
    };

    // My Store settings - Store, Roles and users and Operation tabs - after onboard configuration tabs
    const forStoreSettingNavigationMap = {
      1: storeSettingsSubsteps[0].path,
      2: ManagementSubsteps[0].path,
      3: OperationSubsteps[0].path,
    };

    // Species and Product setup - after onboard configuration tabs
    const forSpeceisAndProductSetupNavigationMap = {
      1: speciesSubsteps[0].path,
      2: productSubsteps[0].path,
    };

    let targetPath;
    if (fromSettings) {
      if (forStoreSetting) {
        // For My store settings - Store, Roles & Users and Operations
        targetPath =
          forStoreSettingNavigationMap[
            tab as keyof typeof forStoreSettingNavigationMap
          ];
        setBreadcrumbs(storeBreadcrumbs);
      } else {
        // For My store settings - Species and Product Set up
        targetPath =
          fromSettingsNavigationMap[
            tab as keyof typeof forSpeceisAndProductSetupNavigationMap
          ];
        setBreadcrumbs(storeSpeciesAndProductBreadcrumbs);
      }
    } else {
      // For Onboarding Process
      targetPath = navigationMap[tab as keyof typeof navigationMap];
      setBreadcrumbs(speciesBreadCrumbs);
    }
    // Navigating after getting the target path
    if (targetPath) {
      if (fromSettings) {
        navigate(targetPath, { state: { fromSettings: true } });
      } else {
        navigate(targetPath);
      }
    }
  };

  return (
    <div className="mb-4">
      {/* Back Navigation */}
      {!fromSettings && (
        <div className="flex items-center mb-2">
          <button
            type="button"
            onClick={onBack || (() => navigate(-1))}
            className="text-gray-700 hover:text-primary text-lg mr-2 focus:outline-none"
          >
            <FontAwesomeIcon icon={faAngleLeft} />
          </button>
          <h1 className="font-semibold">{title}</h1>
        </div>
      )}
      {/* Main Navigation */}
      <div>
        <ul className="grid grid-flow-col text-center border-b border-gray-200 text-gray-500">
          <ul className="flex">
            {breadcrumbs?.map((step) => (
              <li key={step.stepId}>
                <button
                  type="button"
                  className={`flex justify-center border-b-2 px-4 py-2 font-semibold ${
                    activeMainNav === step.name
                      ? 'border-primary text-primary'
                      : 'border-transparent hover:text-primary hover:border-primary'
                  }`}
                  onClick={() => handleMainNav(step.stepId)}
                >
                  {step.name}
                </button>
              </li>
            ))}
          </ul>
        </ul>
      </div>

      {/* Sub Navigation */}
      <nav className="flex items-center text-sm text-gray-500 mt-2 flex-nowrap px-4">
        {subNavSteps.map(
          (
            step: { name: string; step: number; path: string },
            index: number,
          ) => (
            <React.Fragment key={step.step}>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => {
                    if (fromSettings) {
                      navigate(step.path, { state: { fromSettings: true } });
                    } else {
                      navigate(step.path);
                    }
                  }}
                  className="flex items-center"
                >
                  {activeStep >= step.step ? (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-primary text-sm"
                    />
                  ) : (
                    <div className="w-3 h-3 border border-gray-400 bg-gray-400 rounded-full flex justify-center items-center text-[10px]">
                      {step.step}
                    </div>
                  )}
                  <span
                    className={`ml-2 font-semibold truncate max-w-[150px] overflow-hidden ${
                      activeStep === step.step ? 'text-black' : 'text-gray-400'
                    }`}
                  >
                    {step.name}
                  </span>
                </button>
              </div>
              {index < subNavSteps.length - 1 && (
                <span className="mx-3 w-8 border-t-2 border-gray-500"></span>
              )}
            </React.Fragment>
          ),
        )}
      </nav>
    </div>
  );
};
